import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import MobileLayout from "../../components/MobileLayout";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import { Upload, CheckCircle, XCircle, Clock, Camera } from "lucide-react";

const requiredDocKeys = ["aadhaar_front", "aadhaar_back", "pan_card", "selfie"];

const initialFilesState = {
  aadhaar_front: null,
  aadhaar_back: null,
  pan_card: null,
  selfie: null,
};

const initialProgressState = {
  aadhaar_front: { uploaded: 0, total: 0, status: "idle" },
  aadhaar_back: { uploaded: 0, total: 0, status: "idle" },
  pan_card: { uploaded: 0, total: 0, status: "idle" },
  selfie: { uploaded: 0, total: 0, status: "idle" },
};

const MAX_BYTES = 10 * 1024 * 1024;

const KYC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [kycStatus, setKycStatus] = useState(null);
  const [formData, setFormData] = useState({
    aadhaarNumber: "",
    panNumber: "",
  });

  const [files, setFiles] = useState(initialFilesState);
  const [previews, setPreviews] = useState({});
  const [progress, setProgress] = useState(initialProgressState);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const objectUrlsRef = useRef(new Set());

  useEffect(() => {
    fetchKYCStatus();
    return () => {
      objectUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      objectUrlsRef.current.clear();
    };
  }, []);

  const fetchKYCStatus = async () => {
    try {
      const res = await api.get("/kyc/status");
      setKycStatus(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleFilePicked = (key, file) => {
    setError(null);
    if (!file) return;

    if (file.size > MAX_BYTES) {
      setError("File too large (max 10MB)");
      return;
    }

    setFiles((p) => ({ ...p, [key]: file }));

    if (previews[key]) {
      URL.revokeObjectURL(previews[key]);
      objectUrlsRef.current.delete(previews[key]);
    }

    const url = URL.createObjectURL(file);
    objectUrlsRef.current.add(url);
    setPreviews((p) => ({ ...p, [key]: url }));
    setProgress((p) => ({
      ...p,
      [key]: { uploaded: 0, total: file.size, status: "idle" },
    }));
  };

  const handleFileChange = (key) => (e) => {
    const file = e.target.files?.[0];
    handleFilePicked(key, file);
  };

  // -----------------------------
  // PRESIGN (NO content_type)
  // -----------------------------
  const requestPresign = async () => {
    const payload = {
      files: requiredDocKeys.map((key) => ({
        name: key,
        filename: files[key].name,
      })),
    };

    const res = await api.post("/kyc/presign", payload);
    return res.data.uploads;
  };

  // -----------------------------
  // UPLOAD TO S3 (NO HEADERS)
  // -----------------------------
  const uploadFileToPresignedUrl = (putUrl, file, key) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", putUrl, true);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          setProgress((p) => ({
            ...p,
            [key]: {
              uploaded: e.loaded,
              total: e.total,
              status: "uploading",
            },
          }));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setProgress((p) => ({
            ...p,
            [key]: { ...p[key], status: "done" },
          }));
          resolve();
        } else {
          reject(new Error(`Upload failed ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error("Network error"));

      // 🚨 NO Content-Type, NO headers
      xhr.send(file);
    });
  };

  const uploadAllFiles = async () => {
    const presigned = await requestPresign();

    await Promise.all(
      presigned.map((info, idx) =>
        uploadFileToPresignedUrl(
          info.put_url,
          files[requiredDocKeys[idx]],
          requiredDocKeys[idx]
        )
      )
    );

    const urls = {};
    presigned.forEach((u, i) => {
      urls[requiredDocKeys[i]] = u.public_url;
    });

    return urls;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const aadhaarRE = /^\d{12}$/;
      const panRE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

      if (!aadhaarRE.test(formData.aadhaarNumber)) {
        throw new Error("Invalid Aadhaar number");
      }
      if (!panRE.test(formData.panNumber.toUpperCase())) {
        throw new Error("Invalid PAN number");
      }

      const urls = await uploadAllFiles();

      await api.post("/kyc/submit", {
        aadhaarNumber: formData.aadhaarNumber,
        panNumber: formData.panNumber.toUpperCase(),
        documents: urls,
      });

      alert("KYC submitted successfully");
      fetchKYCStatus();
    } catch (err) {
      console.error(err);
      setError(err.message || "KYC submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const renderStatusIcon = () => {
    switch (kycStatus?.status) {
      case "Approved":
        return <CheckCircle className="w-14 h-14 text-green-600" />;
      case "Pending":
        return <Clock className="w-14 h-14 text-yellow-500" />;
      case "Rejected":
        return <XCircle className="w-14 h-14 text-red-600" />;
      default:
        return <Upload className="w-14 h-14 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <MobileLayout showBottomNav={false}>
        <div className="flex justify-center items-center h-full">
          <div className="animate-spin h-10 w-10 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout showBottomNav={false}>
      <div className="p-4 space-y-4">
        <button onClick={() => navigate(-1)} className="text-primary text-sm">
          ← Back
        </button>

        <div className="bg-white p-4 rounded-xl border text-center">
          <div className="flex justify-center mb-3">{renderStatusIcon()}</div>
          <h2 className="font-semibold text-lg">{kycStatus?.status}</h2>
          <p className="text-sm text-gray-600">{kycStatus?.remarks}</p>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded">
            {error}
          </div>
        )}

        {kycStatus?.status !== "Approved" && (
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="text"
              placeholder="Aadhaar Number"
              value={formData.aadhaarNumber}
              onChange={(e) =>
                setFormData({ ...formData, aadhaarNumber: e.target.value })
              }
              className="w-full p-3 border rounded"
              maxLength={12}
              required
            />

            <input
              type="text"
              placeholder="PAN Number"
              value={formData.panNumber}
              onChange={(e) =>
                setFormData({ ...formData, panNumber: e.target.value.toUpperCase() })
              }
              className="w-full p-3 border rounded"
              maxLength={10}
              required
            />

            {requiredDocKeys.map((key) => (
              <label key={key} className="block bg-white p-3 border rounded">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{key.replace("_", " ")}</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    id={`file-${key}`}
                    onChange={handleFileChange(key)}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      document.getElementById(`file-${key}`).click()
                    }
                    className="text-xs bg-indigo-50 px-2 py-1 rounded"
                  >
                    Choose
                  </button>
                </div>
                {previews[key] && (
                  <img
                    src={previews[key]}
                    alt={key}
                    className="mt-2 max-h-40 rounded"
                  />
                )}
              </label>
            ))}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-primary text-white py-3 rounded"
            >
              {submitting ? "Submitting..." : "Submit KYC"}
            </button>
          </form>
        )}
      </div>
    </MobileLayout>
  );
};

export default KYC;
